import {
  EGG_BY_ID,
  ENEMY_BY_ID,
  ITEM_BY_ID,
  UPGRADE_BY_ID,
  WEAPON_BY_ID,
} from "./content";
import type {
  BattleConfig,
  BattlePhase,
  BattleState,
  EnemyKind,
} from "./types";
import { TICK_RATE } from "./types";
import { challengeTypeForWave } from "./waves";

export const SAVE_VERSION = 2;
export const MAX_SAVE_BYTES = 500_000;

const VALID_PHASES = new Set<BattlePhase>([
  "combat",
  "paused",
  "loot",
  "levelup",
  "shop",
  "victory",
  "gameover",
]);

export interface RunSave {
  version: typeof SAVE_VERSION;
  savedAt: number;
  config: BattleConfig;
  state: BattleState;
}

export function encodeRun(
  state: BattleState,
  config: BattleConfig,
  savedAt = Date.now(),
): string {
  assertValidState(state);
  assertValidConfig(config);
  const encoded = JSON.stringify({
    version: SAVE_VERSION,
    savedAt,
    config,
    state,
  } satisfies RunSave);
  if (new TextEncoder().encode(encoded).byteLength > MAX_SAVE_BYTES) {
    throw new Error("Run save exceeds the safe storage limit.");
  }
  return encoded;
}

export function decodeRun(encoded: string): RunSave | null {
  if (new TextEncoder().encode(encoded).byteLength > MAX_SAVE_BYTES) return null;
  try {
    const value: unknown = JSON.parse(encoded);
    if (!isRecord(value)) return null;
    const normalized = value.version === 1 ? migrateV1Run(value) : value;
    if (!normalized || normalized.version !== SAVE_VERSION) return null;
    if (!isFiniteNumber(normalized.savedAt)) return null;
    if (!isRecord(normalized.config) || !isRecord(normalized.state)) return null;
    assertValidConfig(normalized.config as unknown as BattleConfig);
    assertValidState(normalized.state as unknown as BattleState);
    const run = normalized as unknown as RunSave;
    run.state.hordeWave =
      challengeTypeForWave(
        run.state.seed,
        run.state.dangerLevel,
        run.state.wave,
      ) === "horde";
    return run;
  } catch {
    return null;
  }
}

function migrateV1Run(value: Record<string, unknown>): Record<string, unknown> | null {
  if (!isFiniteNumber(value.savedAt) || !isRecord(value.config) || !isRecord(value.state)) {
    return null;
  }

  const legacyState = value.state;
  if (!isRecord(legacyState.player)) return null;
  const legacyPlayer = legacyState.player;
  const waveSeconds = isFiniteNumber(value.config.waveSeconds)
    ? value.config.waveSeconds
    : 30;
  let nextEntityId = nextAvailableLegacyId(legacyState);
  const upgradeChoices = Array.isArray(legacyState.upgradeChoices)
    ? legacyState.upgradeChoices.map((definitionId) => ({
        id: nextEntityId++,
        definitionId: typeof definitionId === "string" ? definitionId : "",
        tier: 1,
      }))
    : legacyState.upgradeChoices;
  const projectiles = migrateLegacyArray(legacyState.projectiles, (projectile) => ({
    ...projectile,
    remainingBounces: 0,
    explosionRadius: 0,
    behavior: "projectile",
  }));
  const pickups = migrateLegacyArray(legacyState.pickups, (pickup) => ({
    ...pickup,
    kind: "material",
  }));
  const shopOffers = migrateLegacyArray(legacyState.shopOffers, (offer) => ({
    ...offer,
    tier: 1,
  }));

  return {
    version: SAVE_VERSION,
    savedAt: value.savedAt,
    config: {
      ...value.config,
      dangerLevel: 0,
      dynamicWaveDuration: false,
    },
    state: {
      ...legacyState,
      nextEntityId,
      waveDurationTicks: Math.max(1, Math.round(waveSeconds * TICK_RATE)),
      treeSpawnTicks: 360,
      upgradeChoices,
      upgradeRefreshes: 0,
      shopOffers,
      dangerLevel: 0,
      hordeWave: false,
      baggedMaterials: 0,
      lootQueue: [],
      weaponClassBonuses: {},
      availableWeaponIds: [...WEAPON_BY_ID.keys()],
      availableItemIds: [...ITEM_BY_ID.keys()],
      player: {
        ...legacyPlayer,
        meleeDamage: 0,
        rangedDamage: 0,
        elementalDamage: 0,
        critChance: 0,
        lifeStealChance: 0,
        range: 0,
        luck: 0,
        harvesting: 0,
        engineering: 0,
        knockback: 0,
        materialsCollected: isFiniteNumber(legacyPlayer.materials)
          ? legacyPlayer.materials
          : 0,
      },
      projectiles,
      pickups,
    },
  };
}

function migrateLegacyArray(
  value: unknown,
  migrate: (entry: Record<string, unknown>) => Record<string, unknown>,
): unknown {
  if (!Array.isArray(value)) return value;
  return value.map((entry) => (isRecord(entry) ? migrate(entry) : entry));
}

function nextAvailableLegacyId(state: Record<string, unknown>): number {
  let maximum = safeInteger(state.nextEntityId, 1)
    ? state.nextEntityId - 1
    : 0;
  for (const key of ["enemies", "projectiles", "pickups", "shopOffers"] as const) {
    const entries = state[key];
    if (!Array.isArray(entries)) continue;
    for (const entry of entries) {
      if (isRecord(entry) && safeInteger(entry.id, 1)) {
        maximum = Math.max(maximum, entry.id);
      }
    }
  }
  return maximum + 1;
}

function assertValidConfig(config: BattleConfig): void {
  if (
    !isFiniteNumber(config.waveSeconds) ||
    config.waveSeconds <= 0 ||
    config.waveSeconds > 600 ||
    !inRange(config.enemyHealthMultiplier, 0.1, 3) ||
    !inRange(config.enemySpeedMultiplier, 0.1, 3) ||
    !inRange(config.enemyDamageMultiplier, 0.1, 3) ||
    (config.dangerLevel !== undefined && !safeInteger(config.dangerLevel, 0, 5)) ||
    (config.startingWeaponId !== undefined &&
      (typeof config.startingWeaponId !== "string" ||
        !WEAPON_BY_ID.has(config.startingWeaponId))) ||
    (config.endless !== undefined && typeof config.endless !== "boolean") ||
    (config.dynamicWaveDuration !== undefined &&
      typeof config.dynamicWaveDuration !== "boolean") ||
    (config.unlockedWeaponIds !== undefined &&
      (!boundedArray(config.unlockedWeaponIds, WEAPON_BY_ID.size) ||
        !config.unlockedWeaponIds.every((id) =>
          typeof id === "string" && WEAPON_BY_ID.has(id)))) ||
    (config.unlockedItemIds !== undefined &&
      (!boundedArray(config.unlockedItemIds, ITEM_BY_ID.size) ||
        !config.unlockedItemIds.every((id) =>
          typeof id === "string" && ITEM_BY_ID.has(id))))
  ) {
    throw new Error("Run save contains an invalid difficulty configuration.");
  }
}

function assertValidState(state: BattleState): void {
  if (
    !safeInteger(state.tick, 0) ||
    !safeInteger(state.seed, 1, 0xffff_ffff) ||
    !safeInteger(state.randomState, 0, 0xffff_ffff) ||
    !safeInteger(state.nextEntityId, 1) ||
    !safeInteger(state.wave, 1, 10_000) ||
    !safeInteger(state.waveTicksRemaining, 0, 36_000) ||
    !safeInteger(state.waveDurationTicks, 1, 36_000) ||
    !safeInteger(state.spawnTicks, 0, 36_000) ||
    !safeInteger(state.treeSpawnTicks, 0, 36_000) ||
    !safeInteger(state.pendingLevelUps, 0, 10_000) ||
    !safeInteger(state.upgradeRefreshes, 0, 10_000) ||
    !safeInteger(state.shopRefreshes, 0, 10_000) ||
    !VALID_PHASES.has(state.phase) ||
    !isRecord(state.arena) ||
    !inRange(state.arena.width, 100, 10_000) ||
    !inRange(state.arena.height, 100, 10_000) ||
    typeof state.endless !== "boolean" ||
    typeof state.hordeWave !== "boolean" ||
    typeof state.specialEnemySpawned !== "boolean" ||
    !safeInteger(state.dangerLevel, 0, 5) ||
    !safeInteger(state.baggedMaterials, 0, 1_000_000_000) ||
    !isRecord(state.weaponClassBonuses) ||
    !boundedNumbers(state.weaponClassBonuses, Object.keys(state.weaponClassBonuses)) ||
    !boundedArray(state.availableWeaponIds, WEAPON_BY_ID.size) ||
    !state.availableWeaponIds.every((id) =>
      typeof id === "string" && WEAPON_BY_ID.has(id)) ||
    !boundedArray(state.availableItemIds, ITEM_BY_ID.size) ||
    !state.availableItemIds.every((id) =>
      typeof id === "string" && ITEM_BY_ID.has(id)) ||
    !isRecord(state.player) ||
    !finitePlayer(state.player as unknown as Record<string, unknown>) ||
    !boundedArray(state.enemies, 240) ||
    !state.enemies.every(finiteEnemy) ||
    !boundedArray(state.projectiles, 320) ||
    !state.projectiles.every(finiteProjectile) ||
    !boundedArray(state.pickups, 220) ||
    !state.pickups.every(finitePickup) ||
    !boundedArray(state.weapons, 8) ||
    !state.weapons.every(finiteWeapon) ||
    !boundedArray(state.items, 1_000) ||
    !state.items.every((item) => typeof item === "string" && ITEM_BY_ID.has(item)) ||
    !boundedArray(state.shopOffers, 4) ||
    !state.shopOffers.every(finiteShopOffer) ||
    !boundedArray(state.upgradeChoices, 4) ||
    !state.upgradeChoices.every(finiteUpgradeOffer) ||
    !boundedArray(state.lootQueue, 220) ||
    !state.lootQueue.every(finiteLootReward) ||
    typeof state.eggId !== "string" ||
    !EGG_BY_ID.has(state.eggId) ||
    !entityIdsAreUnique(state)
  ) {
    throw new Error("Run save contains invalid or unbounded state.");
  }
}

function finiteEnemy(value: unknown): boolean {
  if (
    !isRecord(value) ||
    !isRecord(value.position) ||
    !isRecord(value.velocity)
  ) {
    return false;
  }
  return (
    typeof value.kind === "string" &&
    ENEMY_BY_ID.has(value.kind as EnemyKind) &&
    finiteVector(value.position) &&
    finiteVector(value.velocity) &&
    safeInteger(value.id, 1) &&
    inRange(value.radius, 1, 500) &&
    inRange(value.health, 0, 1_000_000_000) &&
    inRange(value.maxHealth, 1, 1_000_000_000) &&
    boundedNumbers(value, [
      "speed",
      "contactDamage",
      "materialValue",
      "hitFlashTicks",
      "aiTicks",
    ])
  );
}

function finiteProjectile(value: unknown): boolean {
  if (
    !isRecord(value) ||
    !isRecord(value.position) ||
    !isRecord(value.velocity)
  ) {
    return false;
  }
  return (
    finiteVector(value.position) &&
    finiteVector(value.velocity) &&
    safeInteger(value.id, 1) &&
    inRange(value.radius, 0.1, 500) &&
    inRange(value.lifetimeTicks, 0, 36_000) &&
    safeInteger(value.remainingPierces, -1, 100) &&
    safeInteger(value.remainingBounces, 0, 100) &&
    inRange(value.explosionRadius, 0, 10_000) &&
    (value.behavior === "projectile" ||
      value.behavior === "melee" ||
      value.behavior === "explosive" ||
      value.behavior === "bouncing" ||
      value.behavior === "orbiting" ||
      value.behavior === "structure") &&
    boundedNumbers(value, [
      "damage",
    ]) &&
    typeof value.color === "string" &&
    value.color.length <= 40 &&
    (value.shape === "round" ||
      value.shape === "needle" ||
      value.shape === "blade" ||
      value.shape === "orb") &&
    Array.isArray(value.hitEnemyIds) &&
    value.hitEnemyIds.length <= 240 &&
    value.hitEnemyIds.every((id) => safeInteger(id, 1))
  );
}

function finitePickup(value: unknown): boolean {
  if (
    !isRecord(value) ||
    !isRecord(value.position) ||
    !isRecord(value.velocity)
  ) {
    return false;
  }
  return (
    finiteVector(value.position) &&
    finiteVector(value.velocity) &&
    safeInteger(value.id, 1) &&
    inRange(value.radius, 0.1, 500) &&
    (value.kind === "material" ||
      value.kind === "consumable" ||
      value.kind === "crate") &&
    safeInteger(value.value, 0, 1_000_000)
  );
}

function finiteWeapon(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.definitionId === "string" &&
    WEAPON_BY_ID.has(value.definitionId) &&
    safeInteger(value.tier, 1, 4) &&
    inRange(value.cooldownTicks, -1_000, 36_000)
  );
}

function finiteShopOffer(value: unknown): boolean {
  return (
    isRecord(value) &&
    safeInteger(value.id, 1) &&
    (value.kind === "weapon" || value.kind === "item") &&
    typeof value.definitionId === "string" &&
    (value.kind === "weapon"
      ? WEAPON_BY_ID.has(value.definitionId)
      : ITEM_BY_ID.has(value.definitionId)) &&
    safeInteger(value.tier, 1, 4) &&
    safeInteger(value.price, 0, 1_000_000) &&
    typeof value.sold === "boolean" &&
    typeof value.locked === "boolean"
  );
}

function finiteUpgradeOffer(value: unknown): boolean {
  return (
    isRecord(value) &&
    safeInteger(value.id, 1) &&
    typeof value.definitionId === "string" &&
    UPGRADE_BY_ID.has(value.definitionId) &&
    safeInteger(value.tier, 1, 4)
  );
}

function finiteLootReward(value: unknown): boolean {
  return (
    isRecord(value) &&
    safeInteger(value.id, 1) &&
    typeof value.definitionId === "string" &&
    ITEM_BY_ID.has(value.definitionId) &&
    safeInteger(value.tier, 1, 4)
  );
}

function finitePlayer(player: Record<string, unknown>): boolean {
  const requiredNumbers = [
    "health",
    "maxHealth",
    "speed",
    "armor",
    "damageMultiplier",
    "meleeDamage",
    "rangedDamage",
    "elementalDamage",
    "attackSpeedMultiplier",
    "critChance",
    "lifeStealChance",
    "projectileSpeedMultiplier",
    "range",
    "pickupRadius",
    "dodgeChance",
    "regenerationPerSecond",
    "luck",
    "harvesting",
    "engineering",
    "knockback",
    "regenerationProgress",
    "radius",
    "bonusProjectiles",
    "weaponSlotLimit",
    "invulnerableTicks",
    "level",
    "experience",
    "experienceToNext",
    "materials",
    "materialsCollected",
    "kills",
  ];
  return (
    isRecord(player.position) &&
    isRecord(player.velocity) &&
    finiteVector(player.position) &&
    finiteVector(player.velocity) &&
    requiredNumbers.every(
      (key) =>
        isFiniteNumber(player[key]) &&
        Math.abs(player[key]) <= 1_000_000_000,
    ) &&
    inRange(player.radius, 1, 500) &&
    inRange(player.maxHealth, 1, 1_000_000_000) &&
    inRange(player.health, -1_000_000_000, player.maxHealth as number) &&
    inRange(player.speed, 0, 1_000_000) &&
    inRange(player.damageMultiplier, 0.01, 1_000) &&
    inRange(player.attackSpeedMultiplier, 0.01, 1_000) &&
    inRange(player.projectileSpeedMultiplier, 0.01, 1_000) &&
    inRange(player.critChance, 0, 1) &&
    inRange(player.lifeStealChance, 0, 1) &&
    inRange(player.pickupRadius, 0, 100_000) &&
    inRange(player.dodgeChance, 0, 0.6) &&
    safeInteger(player.bonusProjectiles, 0, 100) &&
    safeInteger(player.weaponSlotLimit, 1, 8) &&
    safeInteger(player.invulnerableTicks, 0, 36_000) &&
    safeInteger(player.level, 1, 10_000) &&
    inRange(player.experience, 0, 1_000_000_000) &&
    inRange(player.experienceToNext, 1, 1_000_000_000) &&
    safeInteger(player.materials, 0, 1_000_000_000) &&
    safeInteger(player.materialsCollected, 0, 1_000_000_000) &&
    safeInteger(player.kills, 0, 1_000_000_000)
  );
}

function finiteVector(value: Record<string, unknown>): boolean {
  return isFiniteNumber(value.x) && isFiniteNumber(value.y);
}

function boundedNumbers(
  value: Record<string, unknown>,
  keys: readonly string[],
): boolean {
  return keys.every(
    (key) =>
      isFiniteNumber(value[key]) && Math.abs(value[key]) <= 1_000_000_000,
  );
}

function boundedArray(value: unknown, maximum: number): boolean {
  return Array.isArray(value) && value.length <= maximum;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function inRange(value: unknown, minimum: number, maximum: number): boolean {
  return isFiniteNumber(value) && value >= minimum && value <= maximum;
}

function safeInteger(
  value: unknown,
  minimum: number,
  maximum = Number.MAX_SAFE_INTEGER,
): value is number {
  return (
    isFiniteNumber(value) &&
    Number.isSafeInteger(value) &&
    value >= minimum &&
    value <= maximum
  );
}

function entityIdsAreUnique(state: BattleState): boolean {
  const ids = [
    ...state.enemies.map((enemy) => enemy.id),
    ...state.projectiles.map((projectile) => projectile.id),
    ...state.pickups.map((pickup) => pickup.id),
    ...state.shopOffers.map((offer) => offer.id),
    ...state.upgradeChoices.map((offer) => offer.id),
    ...state.lootQueue.map((reward) => reward.id),
  ];
  return ids.length === new Set(ids).size;
}
